import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { Timestamp } from 'firebase/firestore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { differenceInDays } from 'date-fns';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

export default function MultiMonthPauseModal() {

    // Selectors
    const ServiceProviderID = useAppSelector(selectEffectiveUID) 
    const LiveSubs = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data)
    const selectedLiveSubId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMultiMonthPauseModal.LiveSubscriptionId)
    const selectedLiveSub = LiveSubs?.filter((data) => data.id === selectedLiveSubId)[0]
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const align2monthlyBoundary = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMultiMonthPauseModal.align2monthlyBoundary)

    // States
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.MultiMonthPauseModalOpen);


    // Add new states for date pickers
    const [startDate, setStartDate] = useState<any>(dayjs().add(1, 'day'));
    const [endDate, setEndDate] = useState<any>(null);
    const remainingPauseDays = selectedLiveSub?.pauseDaysLeft||0;
    const [errorMessage, setErrorMessage] = useState<string>('');


    const monthOptions = React.useMemo(() => {
        const options = [];
        const currentDate = dayjs();
        
        for (let i = 0; i < 2; i++) {
            const date = currentDate.add(i, 'month');
            options.push({
                label: date.format('MMMM YYYY'),
                value: date.format('YYYY-MM'),
                startDate: date.startOf('month'),
                endDate: date.endOf('month'),
                days: date.daysInMonth()
            });
        }
        return options;
    }, []);


    useEffect(() => {
        if(align2monthlyBoundary && monthOptions.length > 0 && selectedLiveSub.billingCycle!== "FlexibleMonthly"){
            setStartDate(monthOptions[0].startDate)
            setEndDate(monthOptions[0].endDate)
        }
    }, [align2monthlyBoundary,open])

    // Add month options state



    // Handle month selection
    const handleMonthSelect = (monthValue: string) => {
        const selectedOption = monthOptions.find(opt => opt.value === monthValue);
        if (!selectedOption) return;

        setStartDate(selectedOption.startDate);
        setEndDate(selectedOption.endDate);
        setErrorMessage('');
    };

    // Modify the date selection UI
    const renderDateSelection = () => {
        if (align2monthlyBoundary && selectedLiveSub.billingCycle!== "FlexibleMonthly") {
            return (
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-gray-600">Select the month for pause duration:</p>
                    <div className="flex flex-wrap gap-2">
                        {monthOptions.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleMonthSelect(option.value)}
                                className={`px-4 py-2 rounded-full cursor-pointer text-sm transition-colors
                                    ${startDate && startDate.format('YYYY-MM') === option.value
                                        ? 'bg-blue-600 text-white' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs font-medium mb-1 text-gray-600">Start Date</p>
                        <DatePicker
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        width: '100%'
                                    }
                                }
                            }}
                        />
                    </div>
                    <div>
                        <p className="text-xs font-medium mb-1 text-gray-600">End Date</p>
                        <DatePicker
                            value={endDate}
                            onChange={(newValue) => {setEndDate(newValue)}}
                            // disablePast
                            format="DD/MM/YYYY"
                            minDate={startDate || undefined}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        width: '100%'
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </LocalizationProvider>
        );
    };

    // Add validation function
    const isDateRangeValid = () => {
        if (!startDate || !endDate) return false;
        const start = dayjs(startDate).toDate();
        const end = dayjs(endDate).toDate();
        const daysDifference = differenceInDays(end, start)+1;
        
        return daysDifference > 0 && daysDifference <= remainingPauseDays;
    };

    // Modal Handling
    function handleClose() {
        resetState()
        Dispatch(closeModal("MultiMonthPauseModalOpen"))
    }

    function resetState() {
        setStartDate(dayjs().add(1, 'day'))
        setEndDate(null)
        setErrorMessage('')
    }

    const { update, updateArrayField } = useBackend(`/ServiceProviders/${ServiceProviderID}/LiveSubscriptions`)

    async function onSubmit(e: any) {
        e.preventDefault();

        if (!startDate || !endDate) {
            setErrorMessage("Please select both start and end dates");
            return;
        }

        // Calculate the number of days in the selected period
        const daysDifference = differenceInDays(dayjs(endDate).toDate(), dayjs(startDate).toDate()) + 1;

        if (daysDifference > remainingPauseDays) {
            setErrorMessage(`Not enough pause days available. Selected period requires ${daysDifference} days but you have ${remainingPauseDays} days.`);
            return;
        }

        if (!isDateRangeValid()) {
            setErrorMessage(`Selected date range exceeds available pause days (${remainingPauseDays} days)`);
            return;
        }

        try {
            // Convert dayjs object to JavaScript Date object
            const startDateJS = dayjs(startDate).toDate();
            const endDateJS = dayjs(endDate).toDate();
            
            // Get the pause duration in days
            const pauseDuration = daysDifference;
            
            // Get current billingEnd and add pause duration
            const currentBillingEnd = selectedLiveSub.billingEnd ? 
                new Date(selectedLiveSub.billingEnd) : 
                new Date();
            
            // Add pause duration days to billing end
            const newBillingEnd = new Date(currentBillingEnd);
            if(align2monthlyBoundary && selectedLiveSub.billingCycle!== "FlexibleMonthly"){
                // Move to the end of the next month
                newBillingEnd.setMonth(currentBillingEnd.getMonth() + 2);
                newBillingEnd.setDate(0);
                
              
            
                // Setting to 0 moves to the last day of the previous month
            }else{
                newBillingEnd.setDate(currentBillingEnd.getDate() + pauseDuration);
            }

            updateArrayField(selectedLiveSubId, "pauseHistory", { 
                pauseTS: Timestamp.fromDate(startDateJS), 
                resumeTS: Timestamp.fromDate(endDateJS) 
            })
            
            update(selectedLiveSubId, { 
                pauseDaysLeft: remainingPauseDays - pauseDuration,
                billingEnd: Timestamp.fromDate(newBillingEnd),
                updatedAt: Timestamp.fromDate(new Date())
            })
            
            toast.success("Student Paused Successfully")
        } catch (error) {
            console.log(error)
            setErrorMessage("Error Pausing Student");
        }

        Dispatch(toggleLoading(false))
        handleClose()
        return
    }

    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '16px',
                },
            }}
            maxWidth="md"
            open={open}

            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: "40px", paddingY: "40px" }} className={"bg-white"}>
                <div className={"header pb-4 w-[400px]"}>
                    <p className={"text-cf_heading font-semibold"}>Pause Subscriber</p>
                    <p className={" font-medium mt-1 text-blue-600"}>
                        Remaining Pause Days: {remainingPauseDays}
                    </p>
                </div>

                <div className={"absolute right-2 top-2"}>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <div className={"pt-0 flex flex-col gap-3"}>
                    {renderDateSelection()}
                </div>
                {errorMessage && (
                    <p className="text-red-500 text-sm mt-2.5">{errorMessage}</p>
                )}

                <div className={"flex gap-2 pt-6"}>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            handleClose()
                        }}
                        disabled={isLoading}
                        className={`w-full py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-md ${isLoading ? "cursor-not-allowed" : "hover:bg-gray-200"}`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={isLoading}
                        className={`w-full py-1.5 text-sm bg-blue-600 text-white rounded-md flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "hover:bg-blue-700"}`}
                    >
                        {isLoading && <CircularProgress sx={{ color: "white" }} size={16} />}
                        <p className={isLoading ? "ml-2" : ""}>{isLoading ? "Loading" : "Confirm"}</p>
                    </button>
                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
}